// The composer (app flow §4, design brief §5). Writing feels like annotating a page: the reference
// resolves as you type and the passage sets itself in the serif; you can quote just a phrase; a live
// preview shows the post exactly as friends will see it. Drafts survive a reload. ⌘/Ctrl+Enter posts.
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useId, useMemo, useState } from "react";
import { Chip } from "../../components/Badge";
import { Button, IconButton } from "../../components/Button";
import { SegmentedControl } from "../../components/Tabs";
import { PhotoPicker, usePhotos } from "../../components/PhotoPicker";
import { PhrasePicker, phraseOf, type Range } from "../../components/PhrasePicker";
import { Post } from "../../components/Post";
import ui from "../../components/ui.module.css";
import { SAMPLE_REFERENCES } from "../../data/passages";
import { useConfig, useCreatePost, usePassage, useSession, useVotd } from "../../data/queries";
import { ApiError } from "../../data/problem";
import { LIMITS, type NewImage, type NewPost, type Post as PostT, type Visibility } from "../../data/types";
import { useDraft } from "../../lib/useDraft";
import { useIndicator } from "../../lib/useIndicator";
import { useTitle } from "../../lib/useTitle";
import s from "./new.module.css";

type Kind = "verse" | "note" | "votd";
const KINDS: { kind: Kind; label: string }[] = [
  { kind: "verse", label: "Verse" },
  { kind: "note", label: "Note" },
  { kind: "votd", label: "Today’s verse" },
];

export const Route = createFileRoute("/_app/new")({
  validateSearch: (search: Record<string, unknown>): { kind?: Kind; ref?: string } => ({
    ...(search.kind === "note" || search.kind === "votd" || search.kind === "verse" ? { kind: search.kind } : {}),
    ...(typeof search.ref === "string" && search.ref.length < 40 ? { ref: search.ref } : {}),
  }),
  component: Composer,
});

type Draft = { reference: string; body: string; title: string; visibility: Visibility };
const EMPTY: Draft = { reference: "", body: "", title: "", visibility: "friends" };

function Composer() {
  const { kind = "verse", ref: seededRef } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: session } = useSession();
  const { data: votd } = useVotd();
  const { data: config } = useConfig();
  const create = useCreatePost();
  const [draft, setDraft, clearDraft] = useDraft<Draft>(`compose:${kind}`, seededRef ? { ...EMPTY, reference: seededRef } : EMPTY);
  useEffect(() => {
    if (seededRef && draft.reference !== seededRef) setDraft({ ...draft, reference: seededRef });
    // Only when a new passage arrives from the palette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededRef]);
  const [range, setRange] = useState<Range>(null);
  const tabsRef = useIndicator<HTMLDivElement>();
  const reference = useDeferredValue(draft.reference);
  const passage = usePassage(kind === "votd" ? "" : reference, 5);
  const MAX_BODY = kind === "note" ? LIMITS.note : LIMITS.reflection;
  const ids = { ref: useId(), body: useId(), title: useId(), count: useId() };
  useTitle(kind === "note" ? "Share a note" : "Share a verse");

  const closed = config?.social.state !== undefined && config.social.state !== "on";
  // Photos (FW-007): only while the social.images switch is on.
  const photosOn = config?.features?.["social.images"]?.state === "on";
  const photos = usePhotos();
  const attached = photosOn && photos.photos.length > 0;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  const input: NewPost | null = useMemo(() => {
    if (kind === "verse") {
      const p = passage.data;
      return p ? { kind: "verse", refKey: draft.reference, translation: p.translation, quote: range ? phraseOf(p.text, range) : undefined, body: draft.body.trim(), visibility: draft.visibility } : null;
    }
    if (kind === "note")
      return draft.body.trim()
        ? { kind: "note", title: draft.title.trim(), body: draft.body.trim(), refKey: passage.data ? draft.reference : undefined, visibility: draft.visibility }
        : null;
    return votd ? { kind: "votd_repost", body: draft.body.trim(), visibility: draft.visibility } : null;
  }, [kind, passage.data, draft, range, votd]);

  /** What is posted: the words, and the photos once each is sent and described. */
  const ready: NewImage[] | null = attached ? photos.images() : [];
  const toPost: NewPost | null = input && ready ? (ready.length ? { ...input, images: ready } : input) : null;

  const preview: PostT | null = useMemo(() => {
    if (!session || !input) return null;
    const base = {
      id: "preview",
      author: session.user,
      visibility: draft.visibility,
      body: draft.body.trim() || null,
      title: null,
      ref: null,
      translation: null,
      quote: null,
      work: null,
      votdKey: null,
      reactions: { amen: 0, praying: 0, love: 0, insight: 0 },
      myReaction: null,
      commentCount: 0,
      saved: false,
      createdAt: new Date().toISOString(),
      editedAt: null,
      canEdit: false,
      canDelete: false,
      images: attached ? photos.previews : [],
    };
    if (kind === "verse" && passage.data)
      return { ...base, kind: "verse", ref: { key: passage.data.key, keyVersion: 1, label: passage.data.label }, translation: passage.data.translation, quote: phraseOf(passage.data.text, range) };
    if (kind === "note")
      return {
        ...base,
        kind: "note",
        title: draft.title.trim() || null,
        ref: passage.data ? { key: passage.data.key, keyVersion: 1, label: passage.data.label } : null,
      };
    if (votd) return { ...base, kind: "votd_repost", ref: { ...votd.ref, keyVersion: 1 }, translation: votd.translation, quote: votd.quote, votdKey: votd.key };
    return null;
  }, [session, input, kind, passage.data, range, draft, votd, attached, photos.previews]);

  const submit = async () => {
    if (!toPost || create.isPending || closed) return;
    const done = (p: PostT) => {
      clearDraft();
      void navigate({ to: "/p/$postId", params: { postId: p.id } });
    };
    try {
      done(await create.mutateAsync(toPost));
    } catch (e) {
      // A photo the server no longer has (unused for a day, say): send just those again, then post once more.
      if (!(e instanceof ApiError) || e.type !== "image-unavailable" || !toPost.images) return;
      const again = e.paths.map((p) => Number(/^images.(d+).id$/.exec(p)?.[1])).filter((n) => Number.isInteger(n));
      const uploads = await photos.resend(again);
      const images = photos.images(uploads);
      if (!images) return;
      try {
        done(await create.mutateAsync({ ...toPost, images }));
      } catch {
        // Shown under the form (create.error), with the words and photos kept.
      }
    }
  };

  const notFound = kind === "verse" && reference.trim().length > 2 && passage.isSuccess && !passage.data;

  return (
    <div className={s.page}>
      <div className={ui.pageHead}>
        <IconButton label="Close" icon="close" look="outline" onClick={() => router.history.back()} />
        <h1 className={s.heading}>{kind === "note" ? "Share a note" : "Share a verse"}</h1>
      </div>

      <div className={ui.tabs} ref={tabsRef} role="tablist" aria-label="What are you sharing?">
        {KINDS.map((k) => (
          <Link key={k.kind} to="/new" search={{ kind: k.kind }} replace role="tab" aria-selected={kind === k.kind} className={ui.tab}>
            {k.label}
          </Link>
        ))}
      </div>

      <div className={s.layout}>
        <form
          className={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        >
          {kind === "verse" && (
            <div>
              <label htmlFor={ids.ref} className={ui.label}>
                Passage
              </label>
              <input
                id={ids.ref}
                className={`${ui.field} ${s.reference}`}
                placeholder="Romans 8:28"
                autoComplete="off"
                spellCheck={false}
                value={draft.reference}
                onChange={(e) => {
                  set({ reference: e.target.value });
                  setRange(null);
                }}
                aria-describedby={`${ids.ref}-hint`}
              />
              {!draft.reference && (
                <div className={s.suggestions} aria-label="Try one of these">
                  {SAMPLE_REFERENCES.map((r) => (
                    <Chip key={r} onClick={() => set({ reference: r })}>
                      {r}
                    </Chip>
                  ))}
                </div>
              )}
              <p id={`${ids.ref}-hint`} className={notFound ? ui.error : ui.hint} aria-live="polite">
                {notFound
                  ? "We can’t find that passage. Try a reference like Romans 8:28, up to five verses."
                  : "Up to five verses. Shared from the World English Bible, which is free for anyone to quote."}
              </p>
            </div>
          )}

          {kind === "verse" && passage.data && (
            <div key={passage.data.key} className={s.resolved}>
              <p className={`${ui.eyebrow} ${s.passageHead}`}>
                {passage.data.label} · {passage.data.translation}
              </p>
              <PhrasePicker text={passage.data.text} range={range} onChange={setRange} />
            </div>
          )}

          {kind === "votd" && votd && (
            <div>
              <p className={`${ui.eyebrow} ${s.passageHead}`}>
                Today’s verse · {votd.ref.label} · {votd.translation}
              </p>
              <blockquote className={s.votdQuote}>{votd.quote}</blockquote>
            </div>
          )}

          {kind === "note" && (
            <div>
              <label htmlFor={`${ids.ref}-note`} className={ui.label}>
                About a passage <span className={s.optional}>optional</span>
              </label>
              <input
                id={`${ids.ref}-note`}
                className={ui.field}
                placeholder="Isaiah 40:31"
                autoComplete="off"
                spellCheck={false}
                value={draft.reference}
                onChange={(e) => set({ reference: e.target.value })}
              />
              <p className={ui.hint} aria-live="polite">
                {passage.data ? `Anchored to ${passage.data.label}.` : "Friends can open the passage from your note."}
              </p>
            </div>
          )}

          {kind === "note" && (
            <div>
              <label htmlFor={ids.title} className={ui.label}>
                Title <span className={s.optional}>optional</span>
              </label>
              <input id={ids.title} className={`${ui.field} ${s.title}`} maxLength={120} value={draft.title} onChange={(e) => set({ title: e.target.value })} />
            </div>
          )}

          <div>
            <label htmlFor={ids.body} className={ui.label}>
              {kind === "note" ? "Note" : "Your reflection"} {kind !== "note" && <span className={s.optional}>optional</span>}
            </label>
            <textarea
              id={ids.body}
              className={ui.area}
              rows={kind === "note" ? 9 : 4}
              maxLength={MAX_BODY}
              placeholder={kind === "note" ? "What have you been thinking about?" : "What does it mean to you today?"}
              value={draft.body}
              onChange={(e) => set({ body: e.target.value })}
              aria-describedby={ids.count}
            />
            <p id={ids.count} className={s.count} aria-live="polite">
              {draft.body.length > MAX_BODY - 200 ? `${MAX_BODY - draft.body.length} characters left` : ""}
            </p>
          </div>

          {photosOn && <PhotoPicker state={photos} />}

          <div className={s.footer}>
            <SegmentedControl
              label="Who sees it"
              value={draft.visibility}
              onChange={(v) => set({ visibility: v })}
              options={[
                { value: "friends", label: "Friends", icon: "people" },
                { value: "private", label: "Only me", icon: "lock" },
              ]}
            />
            <Button type="submit" variant="primary" busy={create.isPending} disabled={!toPost || create.isPending || closed}>
              {create.isPending ? "Posting…" : "Post"}
            </Button>
          </div>
          {attached && input && !ready && (
            <p className={ui.hint} aria-live="polite">
              {photos.busy ? "Your photos are still being sent." : photos.failed ? "One photo didn’t send. Try it again or remove it." : "Describe each photo before posting."}
            </p>
          )}
          {closed && <p className={ui.notice}>Posting is paused while Fellowship is {config?.social.state === "maintenance" ? "under maintenance" : "closed"}.</p>}
          {create.isError && (
            <p className={ui.error} role="alert">
              {create.error.message}
            </p>
          )}
          <p className={s.kbd}>
            <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to post · drafts are kept on this device
          </p>
        </form>

        <section className={s.preview} aria-label="Preview">
          <p className={s.previewHead}>{draft.visibility === "private" ? "As it will look, for you alone" : "As friends will see it"}</p>
          {preview ? (
            <Post post={preview} variant="preview" />
          ) : (
            <p className={s.previewEmpty}>{kind === "note" ? "Your note will appear here as friends will see it." : "Choose a passage to see your post as friends will."}</p>
          )}
        </section>
      </div>
    </div>
  );
}
